import Link from 'next/link';
import type { ReactNode } from 'react';

import { Icon } from '@/components/ui/icon';
import { cn } from '@/lib/utils';
import type { ComponentSize } from '@/types/general';

/**
 * Shared footer-style text link used across the auth screens (e.g. the sign-in
 * "forgot password" link and the forgot-password "back to sign in" link). The
 * interaction styles (hover/active/focus/motion) are identical between screens;
 * each caller passes its own typography + layout classes via `className`.
 */

const INTERACTION_CLASS
  = 'rounded-sm transition-colors duration-[var(--duration-short-3)] ease-[var(--ease-emphasized)] hover:text-secondary/80 active:text-secondary/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 motion-reduce:transition-none';

type AuthTextLinkProps = {
  href: string;
  className?: string;
  /** Optional leading icon rendered before the children. */
  icon?: string;
  iconSize?: ComponentSize;
  children: ReactNode;
};

export function AuthTextLink({ href, className, icon, iconSize = 16, children }: AuthTextLinkProps) {
  return (
    <Link href={href} className={cn(className, INTERACTION_CLASS)}>
      {icon !== undefined && <Icon icon={icon} size={iconSize} aria-hidden="true" />}
      {children}
    </Link>
  );
}
